import { Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap, switchMap, take } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { AppStateService } from './app-state.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService implements OnDestroy {
  private defaultLanguage = 'en';
  private supportedLanguages = ['de', 'en'];
  private unsubscribe = new Subject();
  private static LANGUAGE_NAME_PREFIX = 'LANGUAGE_';

  constructor(private translate: TranslateService, private appStateService: AppStateService, titleService: Title) {
    translate.onLangChange
      .pipe(
        takeUntil(this.unsubscribe),
        tap((event) => appStateService.patchLanguage(event.lang)),
        switchMap((_) => translate.get('APP_TITLE'))
      )
      .subscribe((appTitle) => {
        titleService.setTitle(appTitle);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next({});
    this.unsubscribe.complete();
  }

  initialize() {
    this.appStateService
      .getState()
      .pipe(take(1))
      .subscribe((state) => {
        let languageToUse = this.translate.getBrowserLang();
        if (state.selectedLanguage) {
          languageToUse = state.selectedLanguage;
        }

        this.translate.resetLang(this.defaultLanguage);

        this.translate.setDefaultLang(this.defaultLanguage);
        this.translate.addLangs(this.supportedLanguages);
        this.translate.use(languageToUse);
      });
  }

  changeLanguage(lang: string): string {
    this.translate.use(lang);

    return lang;
  }

  getCurrentLanguage(): string {
    return this.translate.currentLang;
  }

  getSupportedLanguages(): string[] {
    return this.translate.getLangs();
  }

  getLanguageName(lang: string): Observable<string> {
    const key = `${LanguageService.LANGUAGE_NAME_PREFIX}${lang.toUpperCase()}`;
    return this.translate.get(key);
  }

  get(text: string, params?: Object) {
    return this.translate.get(text, params);
  }
}
